/***************************************************************************/
"use client";
/***************************************************************************/

import * as ToastPrimitives from "@radix-ui/react-toast";
import { type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "../../../utils/css";
import { ButtonIcon, buttonVariants } from "../button";

const ToastActionContext = React.createContext<{
  loading?: boolean;
  size?: "default" | "sm" | "lg" | null | undefined;
}>({ size: "default" });

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action> &
    VariantProps<typeof buttonVariants>
>(({ className, variant, size, ...props }, ref) => (
  <ToastActionContext.Provider value={{ size: size ?? "sm" }}>
    <ToastPrimitives.Action
      ref={ref}
      className={cn(
        buttonVariants({ variant: variant ?? "outline", size: size ?? "sm" }),
        className,
      )}
      {...props}
    />
  </ToastActionContext.Provider>
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastActionIcon: typeof ButtonIcon = ({ size, ...props }) => {
  const toastActionContextValue = React.useContext(ToastActionContext);
  const derivedSize = size ?? toastActionContextValue.size;

  return <ButtonIcon {...props} size={derivedSize} />;
};

export type ToastActionElement = React.ReactElement<typeof ToastAction>;

export { ToastAction, ToastActionIcon };
